/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import { Loader, Message, Tab, Segment, Dimmer } from "semantic-ui-react";
import { useAuth } from "../../../hooks";
import { Dashboard as DashboardController } from "../../../api";
import { AcademicCycle } from "../../../api";
import {
  DashboardHeader,
  DashboardAlerts,
  QuickStats,
  EnrollmentTab,
  AttendanceTab,
  FinancialTab,
  AcademicTab,
  OperationsTab,
} from "../../../components/Admin/Dashboard";
import "./Dashboard.scss";

const dashboardController = new DashboardController();
const academicCycleController = new AcademicCycle();

export function Dashboard() {
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Data states
  const [overviewData, setOverviewData] = useState(null);
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [academicData, setAcademicData] = useState(null);
  const [teachingData, setTeachingData] = useState(null);
  const [operationalData, setOperationalData] = useState(null);

  // Filter states
  const [academicCycles, setAcademicCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);

  // Load academic cycles for filter
  useEffect(() => {
    (async () => {
      try {
        const response = await academicCycleController.getAcademicCycles(
          true
        );
        setAcademicCycles(response);
        if (response && response.length > 0) {
          const activeCycle =
            response.find((cycle) => cycle.active) || response[0];
          setSelectedCycle(activeCycle.id);
        }
      } catch (error) {
        console.error("Error loading academic cycles:", error);
      }
    })();
  }, [accessToken]);

  // Load dashboard data
  useEffect(() => {
    if (!selectedCycle) return;
    loadDashboardData();
  }, [accessToken, selectedCycle]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = { academicCycleId: selectedCycle };

      // Load all dashboard data in parallel
      const [
        overview,
        enrollment,
        attendance,
        financial,
        academic,
        teaching,
        operations,
      ] = await Promise.allSettled([
        dashboardController.getOverview(filters),
        dashboardController.getEnrollmentAnalytics(filters),
        dashboardController.getAttendanceAnalytics(filters),
        dashboardController.getFinancialAnalytics(filters),
        dashboardController.getAcademicAnalytics(filters),
        dashboardController.getTeachingAnalytics(filters),
        dashboardController.getOperationalMetrics(filters),
      ]);

      // Set data or handle errors for each request
      if (overview.status === "fulfilled") setOverviewData(overview.value);
      if (enrollment.status === "fulfilled")
        setEnrollmentData(enrollment.value);
      if (attendance.status === "fulfilled")
        setAttendanceData(attendance.value);
      if (financial.status === "fulfilled") setFinancialData(financial.value);
      if (academic.status === "fulfilled") setAcademicData(academic.value);
      if (teaching.status === "fulfilled") setTeachingData(teaching.value);
      if (operations.status === "fulfilled")
        setOperationalData(operations.value);

      // Check if all requests failed
      const allFailed = [
        overview,
        enrollment,
        attendance,
        financial,
        academic,
        teaching,
        operations,
      ].every((result) => result.status === "rejected");

      if (allFailed) {
        setError(
          "No se pudieron cargar los datos del dashboard. Por favor, intenta nuevamente."
        );
      }
    } catch (error) {
      console.error("Error loading dashboard:", error);
      setError("Error al cargar el dashboard. Por favor, intenta nuevamente.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboardData();
  };

  const cycleOptions = academicCycles.map((cycle) => ({
    key: cycle.id,
    text: `${cycle.name} - ${cycle.Level?.name}`,
    value: cycle.id,
  }));

  if (loading && !overviewData) {
    return (
      <div className="dashboard-admin">
        <Segment basic>
          <Dimmer active inverted>
            <Loader size="large">Cargando Dashboard...</Loader>
          </Dimmer>
        </Segment>
      </div>
    );
  }

  return (
    <div className="dashboard-admin">
      <DashboardHeader
        cycleOptions={cycleOptions}
        selectedCycle={selectedCycle}
        onCycleChange={setSelectedCycle}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {/* Overview Section - Always visible */}
      {overviewData && (
        <>
          {/* Quick Stats */}
          <QuickStats overviewData={overviewData} />

          {/* Alerts Section */}
          <DashboardAlerts alerts={overviewData.alerts} />
        </>
      )}

      {/* Tabbed Content */}
      <Tab
        activeIndex={activeTab}
        onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
        menu={{ secondary: true, pointing: true }}
        panes={[
          {
            menuItem: {
              key: "enrollment",
              icon: "users",
              content: "Matrícula",
            },
            render: () => <EnrollmentTab enrollmentData={enrollmentData} />,
          },
          {
            menuItem: {
              key: "attendance",
              icon: "calendar check",
              content: "Asistencia",
            },
            render: () => <AttendanceTab attendanceData={attendanceData} />,
          },
          {
            menuItem: {
              key: "financial",
              icon: "dollar",
              content: "Financiero",
            },
            render: () => <FinancialTab financialData={financialData} />,
          },
          {
            menuItem: {
              key: "academic",
              icon: "graduation cap",
              content: "Académico",
            },
            render: () => <AcademicTab academicData={academicData} />,
          },
          {
            menuItem: {
              key: "operations",
              icon: "cogs",
              content: "Operaciones",
            },
            render: () => (
              <OperationsTab
                operationalData={operationalData}
                teachingData={teachingData}
              />
            ),
          },
        ]}
      />
    </div>
  );
}
